import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";

import { getOfferById, updateOffer } from "../../../redux/offer/action";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import CouponForm, { emptyCouponValues } from "./CouponForm";
import "../../Common/FormShell/FormShell.css";

const formatDateForInput = (value) => (value ? value.split("T")[0] : "");

const UpdateOffers = () => {
  const { id } = useParams();
  const { hasAction } = usePermissions();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const offer = useSelector(({ offer: { offerbyid } }) => offerbyid);
  const canEdit = hasAction(MENU.OFFERS, PERMISSION.EDIT);

  useEffect(() => {
    if (id) dispatch(getOfferById(id));
  }, [dispatch, id]);

  const initialValues = useMemo(() => {
    if (!offer?.id || Number(offer.id) !== Number(id)) return null;
    return {
      ...emptyCouponValues,
      coupon_code: offer.coupon_code || "",
      name: offer.name || "",
      description: offer.description || "",
      discount_type: offer.discount_type ?? "",
      discount_value: offer.discount_value ?? "",
      max_discount: offer.max_discount ?? "",
      // offer_for=4 was the old "user-targeted" value; it means "everything"
      // with a customer restriction, so map it to 1 for the form.
      offer_for: Number(offer.offer_for) === 4 ? 1 : offer.offer_for ?? 1,
      product_ids: offer.product_ids || [],
      category_ids: offer.category_ids || [],
      brand_ids: offer.brand_ids || [],
      user_restriction: offer.user_restriction ?? 1,
      user_ids: offer.users || [],
      platform_restriction: offer.platform_restriction ?? 0,
      min_order_value: offer.min_order_value ?? "",
      limit_total_uses: offer.usage_limit != null,
      usage_limit: offer.usage_limit ?? "",
      limit_per_customer: offer.usage_per_user != null,
      usage_per_user: offer.usage_per_user ?? "",
      start_date: formatDateForInput(offer.start_date),
      end_date: formatDateForInput(offer.end_date),
      status: offer.status ?? 1,
      image: offer.image || "",
    };
  }, [offer, id]);

  const handleSubmit = (payload) => {
    dispatch(updateOffer({ id: Number(id), ...payload }, setLoading, navigate));
  };

  const used = offer?.used_count ?? 0;
  const limit = offer?.usage_limit;

  return (
    <div className="nb-page">
      <div className="nb-topbar">
        <button
          type="button"
          className="nb-topbar__back"
          onClick={() => navigate("/offerlist")}
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Coupons
        </button>
        <span className="nb-topbar__sep">/</span>
        <span className="nb-topbar__current">{offer?.name || "Edit coupon"}</span>
      </div>

      <div
        className="nb-hero"
        style={{ maxWidth: 840, margin: "0 auto", padding: "28px 24px 0" }}
      >
        <span className="nb-hero__eyebrow">
          <Tag size={11} aria-hidden="true" />
          Marketing
        </span>
        <h1 className="nb-hero__title">Edit coupon</h1>
        <p className="nb-hero__sub">
          {`Used ${used} ${limit != null ? `of ${limit}` : "times (no total limit)"} so far.`}
          {!canEdit && " You have view-only access — changes can't be saved."}
        </p>
      </div>

      {offer?.id && Number(offer.offer_type) === 1 ? (
        // Legacy "instant" offers predate the coupons-only decision. Editing
        // one here would silently convert it into a coupon, so block that.
        <p style={{ maxWidth: 840, margin: "24px auto", padding: "0 24px" }}>
          This is a legacy instant offer, not a coupon — this screen manages
          coupons only, so it can't be edited here. You can still deactivate it
          from the list, or ask the team to migrate it to a coupon.
        </p>
      ) : initialValues ? (
        <CouponForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          loading={loading || !canEdit}
          isUpdate
          submitLabel={canEdit ? "Save changes" : "View only"}
        />
      ) : (
        <p style={{ maxWidth: 840, margin: "24px auto", padding: "0 24px" }}>
          Loading coupon…
        </p>
      )}
    </div>
  );
};

export default UpdateOffers;
